/** Minimum wait before the same employee can scan again (ms) */
export const SCAN_COOLDOWN_MS = 30000;

/** Ignore duplicate RFID/QR reads within this window (ms) */
export const SCAN_DEBOUNCE_MS = 900;

/** Same value in seconds for backend alignment */
export const SCAN_COOLDOWN_SECONDS = SCAN_COOLDOWN_MS / 1000;

export function isPendingAttendanceLog(log) {
  return typeof log?.id === 'string' && log.id.startsWith('pending-');
}

export function findConfirmedActiveLog(logs, employeeUid) {
  const uid = String(employeeUid || '').toUpperCase();
  if (!uid || !Array.isArray(logs)) return null;

  return logs.find((log) => {
    if (String(log.employeeId || '').toUpperCase() !== uid) return false;
    if (isPendingAttendanceLog(log)) return false;
    if (log.timeOut && log.timeOut !== '' && log.timeOut !== '--:--') return false;
    return true;
  }) || null;
}

export function createScanGate(cooldownMs = SCAN_COOLDOWN_MS) {
  const entries = new Map();

  return {
    check(uid) {
      const key = String(uid || '').toUpperCase();
      if (!key) return { allowed: false, reason: 'invalid' };

      const entry = entries.get(key);
      if (!entry) return { allowed: true, key };

      if (entry.processing) {
        return { allowed: false, reason: 'processing', key };
      }

      const remainingMs = entry.until - Date.now();
      if (remainingMs > 0) {
        return { allowed: false, reason: 'cooldown', key, remainingMs };
      }

      return { allowed: true, key };
    },

    begin(key) {
      entries.set(key, { processing: true, until: 0 });
    },

    complete(key) {
      entries.set(key, { processing: false, until: Date.now() + cooldownMs });
    },

    fail(key) {
      entries.delete(key);
    },
  };
}

export function formatScanWaitMessage(remainingMs) {
  const seconds = Math.max(1, Math.ceil(remainingMs / 1000));
  return `Please wait ${seconds}s before scanning again.`;
}

/** Minimum characters before an RFID keyboard-wedge read is accepted */
export const RFID_MIN_UID_LENGTH = 4;

/** Per-terminal guard for repeated RFID / QR reads of the same UID */
export function createRfidScanGuard(cooldownMs = SCAN_COOLDOWN_MS) {
  let lastUid = '';
  let lockedUntil = 0;

  return {
    canScan(uid) {
      const key = String(uid || '').toUpperCase();
      if (!key || key.length < RFID_MIN_UID_LENGTH) return false;
      if (Date.now() < lockedUntil && lastUid === key) return false;
      return true;
    },

    lock(uid) {
      lastUid = String(uid || '').toUpperCase();
      lockedUntil = Date.now() + cooldownMs;
    },

    remainingMs(uid) {
      const key = String(uid || '').toUpperCase();
      if (lastUid !== key || Date.now() >= lockedUntil) return 0;
      return lockedUntil - Date.now();
    },
  };
}
